import type { ReactNode } from 'react'

import type { BookingPage } from '../../api/transport'
import { formatPesewas } from '../../lib/money'
import { firstName, whenLabel } from './bookingWords'
import { dayLabel, dayOf, timeOf } from './tripTimes'

/**
 * The booking's timeline (M40-T09, spec §C.3.6), shared by Screen 6 and the
 * booking page (T10), fed by the booking page's answer (T08).
 *
 * "Hospital informed" is a staff tick that the hospital was told to expect
 * the passenger. It is never "confirmed": the line says "has been told to
 * expect", and nothing here uses the word for the hospital.
 */

type Step = {
  key: string
  title: string
  done: boolean
  body: ReactNode
}

/** The statuses from the outbound pickup on. `return_requested` can follow
 *  `driver_assigned` directly (the customer's tap, T08), so "under way" never
 *  claims a pickup was recorded. */
const UNDER_WAY: ReadonlyArray<BookingPage['status']> = [
  'picked_up',
  'arrived',
  'return_requested',
  'return_picked_up',
  'completed',
]

function steps(page: BookingPage): Step[] {
  const underWay = UNDER_WAY.includes(page.status)
  const list: Step[] = [
    {
      key: 'paid',
      title: 'Paid',
      done: true,
      body: page.receipt.paid_at
        ? `${formatPesewas(page.receipt.total_pesewas)}, ${whenLabel(page.receipt.paid_at)}`
        : formatPesewas(page.receipt.total_pesewas),
    },
    {
      key: 'hospital',
      title: 'Hospital',
      done: page.hospital_informed,
      body: page.hospital_informed
        ? `${page.hospital} has been told to expect ${firstName(page)} on ${dayLabel(dayOf(new Date(page.pickup_at)))}.`
        : `We'll let ${page.hospital} know you're coming.`,
    },
    {
      key: 'vehicle',
      title: 'Vehicle & attendant',
      done: page.driver !== null,
      body: page.driver ? (
        <>
          {page.driver.name}
          {page.driver.vehicle_plate ? `, ${page.driver.vehicle_plate}` : ''}
          {page.driver.phone && (
            <>
              {', '}
              <a
                href={`tel:${page.driver.phone}`}
                className="font-semibold text-accent underline underline-offset-2"
              >
                {page.driver.phone}
              </a>
            </>
          )}
          {page.driver.attendant_name
            ? `. Attendant: ${page.driver.attendant_name}`
            : ''}
        </>
      ) : (
        `By ${whenLabel(page.driver_details_by)} — names and plate`
      ),
    },
    // Once the trip is under way, the day has come: the day-before reminder
    // is behind them, so it goes (found in the T14 walk).
    ...(underWay
      ? []
      : [
          {
            key: 'reminder',
            title: 'Reminder',
            done: false,
            body: "We'll text you the day before.",
          },
        ]),
    {
      key: 'day',
      title: 'On the day',
      done: underWay,
      body: underWay
        ? 'Your trip is under way. Call your driver directly if you need them.'
        : 'Call your driver directly on the number we send you.',
    },
  ]
  if (page.return_mode === 'call_to_collect') {
    list.push({
      key: 'return',
      title: 'Return trip',
      done: page.return_requested,
      body: page.return_requested
        ? 'You asked for your return. The transport desk has been told.'
        : 'When discharged, tap Ready to collect, or call the transport desk.',
    })
  } else if (page.return_mode === 'fixed' && page.return_pickup_at) {
    list.push({
      key: 'return',
      title: 'Return trip',
      done: page.status === 'return_picked_up' || page.status === 'completed',
      body: `Picked up again at ${timeOf(new Date(page.return_pickup_at))}.`,
    })
  }
  return list
}

export function BookingTimeline({ page }: { page: BookingPage }): ReactNode {
  return (
    <ol aria-label="Your trip, step by step" className="flex flex-col gap-0">
      {steps(page).map((step, i, all) => (
        <li key={step.key} className="flex gap-3">
          <div className="flex flex-col items-center" aria-hidden="true">
            <span
              className={`mt-1 h-3 w-3 shrink-0 rounded-full border-2 ${
                step.done
                  ? 'border-accent bg-accent'
                  : 'border-line-2 bg-bg-app'
              }`}
            />
            {i < all.length - 1 && <span className="w-px flex-1 bg-line-2" />}
          </div>
          <div className="pb-4 text-[13px]">
            <h3 className="font-medium text-ink-1">
              {step.title}
              <span className="sr-only">
                {step.done ? ' (done)' : ' (to come)'}
              </span>
            </h3>
            <p className="text-ink-2">{step.body}</p>
          </div>
        </li>
      ))}
    </ol>
  )
}
